import React, { useState, useMemo } from 'react'
import { View, FlatList, Text as RNText, TouchableOpacity, Image, ScrollView } from 'react-native'
import { useTheme, useNavigation } from '@react-navigation/native'
import {
  Text,
  Icon,
  Header,
  SearchBar,
  IconName,
  GroupAvatar,
  MessageItemSkeletonList,
  Skeleton,
} from '@components'
import { defaultImage } from '@assets'
import getStyle from './style'
import { getCommonStyles, Scope, formatPublishedAt } from '@utils'
import {
  useGetConversationById,
  useGetProfile,
  useInitiateIndividualConversation,
  useMarkConversationRead,
  usePaginateContacts,
  useScreen,
} from '@hooks'
import { useLanguage } from '@context'
import { GroupConversation } from './groupConversation'
import { formatUnreadBadge, getUnreadCount, hasUnread } from './unreadBadge'
import { Conversation as ConversationScreen } from './conversation'
import { SafeAreaView } from 'react-native-safe-area-context'
import { usePaginateConversations, ConversationType } from '@hooks'
import type { Contact, Conversation, ConversationParticipant } from '@hooks'

// Type pour le bouton épinglé
interface PinnedButtonItem {
  id: string
  type: 'button'
}

// Union type pour la liste épinglés
type PinnedListItem = Contact | PinnedButtonItem

export const Messages: React.FC = () => {
  const theme = useTheme()
  const navigation = useNavigation()
  const { isMobile } = useScreen()
  const { t } = useLanguage()
  const commonStyles = getCommonStyles(theme)
  const styles = getStyle(theme)

  // Use Scope enum for tab state
  const [activeTab, setActiveTab] = useState<Scope>(Scope.PRIVATE)
  const [searchText, setSearchText] = useState('')
  const [isPinned, setIsPinned] = useState(false)

  // Map activeTab to API parameters
  const conversationParams = useMemo(() => {
    if (activeTab === Scope.GROUP) {
      return { type: ConversationType.GROUP, search: searchText }
    } else if (activeTab === Scope.PRIVATE || activeTab === Scope.PROFESSIONAL) {
      return {
        type: ConversationType.INDIVIDUAL,
        scope: activeTab,
        search: searchText,
      }
    }
    // OTHERS - ignore for now
    return { type: ConversationType.INDIVIDUAL, search: searchText }
  }, [activeTab, searchText])

  const { flatData: contacts, isLoading: isLoadingContacts } = usePaginateContacts({
    search: searchText,
    scope: activeTab === Scope.PRIVATE ? Scope.PRIVATE : Scope.PROFESSIONAL,
    noGhost: true,
  })
  const {
    flatData: conversations,
    totalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingConversations,
  } = usePaginateConversations(conversationParams)
  const { mutateAsync: initiateIndividualConversation } = useInitiateIndividualConversation()

  // Tab counts - use totalCount for current tab
  const privateCount = activeTab === Scope.PRIVATE ? totalCount : 0
  const proCount = activeTab === Scope.PROFESSIONAL ? totalCount : 0
  const groupsCount = activeTab === Scope.GROUP ? totalCount : 0

  const [selectedConversationId, setSelectedConversationId] = useState<string | undefined>()
  const { data: selectedConversation, isLoading: isLoadingConversation } = useGetConversationById(
    selectedConversationId || '',
  )
  const { mutate: markConversationRead } = useMarkConversationRead()
  const { data: currentUser } = useGetProfile()

  const nbPinned = 0 // TODO: Implement pinned messages feature

  const handleMessagePress = (item: Conversation) => {
    // Opening a conversation marks it as read (clears its unread badge).
    if (hasUnread(item.unreadCount)) {
      markConversationRead(item.id)
    }
    if (!isMobile) {
      setSelectedConversationId(item.id)
      return
    }
    // Check if it's a group conversation (has name or multiple participants)
    const isGroup = item.name !== null || item.participants.length > 2
    if (isGroup) {
      ;(navigation as any).navigate('Messages', {
        screen: 'GroupConversation',
        params: {
          conversationId: item.id,
        },
      })
    } else {
      // For individual conversations, get the other participant (not the current user)
      const other =
        item.participants.find(
          (p: ConversationParticipant) => p.id !== currentUser?.accountId,
        ) || item.participants[0]
      if (other) {
        ;(navigation as any).navigate('Messages', {
          screen: 'Conversation',
          params: {
            conversationId: item.id,
            contactId: other.id,
          },
        })
      }
    }
  }

  const handleContactPress = async (item: Contact) => {
    const conversation = await initiateIndividualConversation({
      participantAccountId: item.accountId,
    })
    setSelectedConversationId(conversation.id)
  }

  const togglePin = () => {
    setIsPinned((isPinned) => !isPinned)
  }

  const getParticipantName = (participant: ConversationParticipant): string => {
    const parts = [participant.fullName].filter(Boolean)
    return parts.join(' ') || 'Unknown'
  }

  const renderMessageItem = ({ item }: { item: Conversation }) => {
    const isGroup = item.name !== null || item.participants.length > 2
    const lastMessageTime = item.lastMessage?.createdAt
      ? formatPublishedAt(item.lastMessage.createdAt)
      : formatPublishedAt(item.updatedAt || item.createdAt)

    const unread = getUnreadCount(item.unreadCount)
    // Last-message line: bold + a counter pill when the conversation is unread.
    const lastMessageLine = (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: theme.spacing.sm,
        }}
      >
        <Text
          type={unread > 0 ? 'bodyBold' : 'body'}
          color={unread > 0 ? theme.colors.black : undefined}
          numberOfLines={1}
          style={{ flex: 1 }}
        >
          {item.lastMessage?.content || 'No messages'}
        </Text>
        {unread > 0 && (
          <View
            style={{
              minWidth: 20,
              height: 20,
              borderRadius: 10,
              paddingHorizontal: 6,
              backgroundColor: theme.colors.primary,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text type="microText" color={theme.colors.white}>
              {formatUnreadBadge(item.unreadCount)}
            </Text>
          </View>
        )}
      </View>
    )

    if (isGroup) {
      const displayName =
        item.name ||
        item.participants
          .map((p: ConversationParticipant) => getParticipantName(p).split(' ')[0])
          .filter(Boolean)
          .join(', ')

      return (
        <TouchableOpacity
          style={[
            styles.messageItem,
            !isMobile &&
              item.id === selectedConversationId && { backgroundColor: theme.colors.grey20 },
          ]}
          onPress={() => handleMessagePress(item)}
        >
          <GroupAvatar profiles={item.participants} />
          <View style={styles.messageContent}>
            <View style={styles.messageHeader}>
              <Text type="bodyBold">{displayName}</Text>
              <Text type="microText" color={theme.colors.greyBlue}>
                {lastMessageTime}
              </Text>
            </View>
            {lastMessageLine}
          </View>
        </TouchableOpacity>
      )
    } else {
      // Individual conversation - show the other participant's avatar
      const other =
        item.participants.find(
          (p: ConversationParticipant) => p.id !== currentUser?.accountId,
        ) || item.participants[0]
      return (
        <TouchableOpacity
          style={[
            styles.messageItem,
            !isMobile &&
              item.id === selectedConversation?.id && { backgroundColor: theme.colors.grey20 },
          ]}
          onPress={() => handleMessagePress(item)}
        >
          <Image
            source={other?.avatarUrl ? { uri: other.avatarUrl } : defaultImage}
            style={styles.avatar}
          />
          <View style={styles.messageContent}>
            <View style={styles.messageHeader}>
              <Text type="bodyBold">{other.fullName}</Text>
              <Text type="microText" color={theme.colors.greyBlue}>
                {lastMessageTime}
              </Text>
            </View>
            {lastMessageLine}
          </View>
        </TouchableOpacity>
      )
    }
  }

  const renderTabButton = (tabKey: Scope, title: string, iconName?: IconName, count?: number) => {
    const isActive = activeTab === tabKey

    // Couleurs spécifiques pour chaque tab
    const getTabColors = () => {
      switch (tabKey) {
        case Scope.PRIVATE:
          return {
            activeColor: theme.colors.green,
            activeBgColor: theme.colors.green + '20', // Vert avec transparence
            activeTextColor: theme.colors.green,
          }
        case Scope.PROFESSIONAL:
          return {
            activeColor: theme.colors.primary,
            activeBgColor: theme.colors.primary + '20', // Bleu avec transparence
            activeTextColor: theme.colors.primary,
          }
        case Scope.GROUP:
          return {
            activeColor: '#FF69B4', // Rose
            activeBgColor: '#FF69B4' + '20', // Rose avec transparence
            activeTextColor: '#FF69B4',
          }
        case Scope.OTHERS:
          return {
            activeColor: theme.colors.greyBlue,
            activeBgColor: theme.colors.greyBlue + '20',
            activeTextColor: theme.colors.greyBlue,
          }
        default:
          return {
            activeColor: theme.colors.primary,
            activeBgColor: theme.colors.primary + '20',
            activeTextColor: theme.colors.primary,
          }
      }
    }

    const colors = getTabColors()

    return (
      <TouchableOpacity
        style={[
          styles.tabButton,
          isActive && {
            //backgroundColor: colors.activeBgColor,
            borderColor: colors.activeColor,
          },
        ]}
        onPress={() => setActiveTab(tabKey)}
      >
        {iconName && (
          <Icon
            name={iconName}
            size={16}
            color={isActive ? colors.activeTextColor : theme.colors.greyBlue}
            style={styles.tabIcon}
          />
        )}
        <RNText
          style={[
            styles.tabButtonText,
            isActive && { fontWeight: 'bold', color: colors.activeTextColor },
          ]}
        >
          {title}
        </RNText>
      </TouchableOpacity>
    )
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={!isMobile ? commonStyles.flex370Bordered : commonStyles.flex}>
        {/* Header avec icône + à droite */}
        <Header
          title={t('tabNavigation.messages')}
          iconRight={'plus'}
          onPressRight={() =>
            (navigation as any).navigate('Messages', { screen: 'AddConversation' })
          }
        />
        <View style={styles.searchContainerMobile}>
          {/* Barre de recherche */}
          <View style={styles.searchContainer}>
            <SearchBar
              value={searchText}
              onChangeText={setSearchText}
              placeholder={t('messaging.searchPlaceholder')}
            />
          </View>

          {/* Tabs : Privé, Pro, Groupes, Autre */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabContainer}>
            {renderTabButton(Scope.PRIVATE, t('messagesScreen.private'), 'lock', privateCount)}
            {renderTabButton(Scope.PROFESSIONAL, t('messagesScreen.pro'), 'work', proCount)}
            {renderTabButton(Scope.GROUP, t('messagesScreen.groups'), 'users', groupsCount)}
            {renderTabButton(Scope.OTHERS, t('messagesScreen.other'), undefined)}
          </ScrollView>
        </View>

        {/* Section des contacts épinglés */}
        <View
          style={[
            styles.pinnedSection,
            {
              borderBottomColor:
                activeTab === Scope.PRIVATE
                  ? theme.colors.green
                  : activeTab === Scope.PROFESSIONAL
                    ? theme.colors.primary
                    : activeTab === Scope.GROUP
                      ? '#FF69B4'
                      : theme.colors.greyBlue,
            },
          ]}
        >
          {isLoadingContacts ? (
            <View style={[styles.pinnedList, commonStyles.rowNoGap]}>
              <View style={styles.pinnedButton}>
                <Skeleton
                  width={50}
                  height={50}
                  borderRadius={25}
                  style={{ marginBottom: theme.spacing.xs }}
                />
                <Skeleton width={44} height={10} borderRadius={5} />
              </View>
              {Array.from({ length: 6 }).map((_, index) => (
                <View key={index} style={styles.pinnedContact}>
                  <Skeleton
                    width={50}
                    height={50}
                    borderRadius={25}
                    style={{ marginBottom: theme.spacing.xs }}
                  />
                  <Skeleton width={50} height={10} borderRadius={5} />
                </View>
              ))}
            </View>
          ) : (
            <FlatList
              horizontal
              data={[{ id: 'pinned-button', type: 'button' }, ...contacts]}
              renderItem={({ item }: { item: PinnedListItem }) => {
                if ('type' in item) {
                  return (
                    <TouchableOpacity style={styles.pinnedButton} onPress={togglePin}>
                      <View style={styles.pinnedButtonContainer}>
                        <Icon name="pin" size={20} color={theme.colors.greyBlue} />
                        {nbPinned > 0 && (
                          <View style={styles.pinnedBadge}>
                            <Text type="bodyExtraSmall" style={styles.pinnedBadgeText}>
                              {nbPinned}
                            </Text>
                          </View>
                        )}
                      </View>
                      <Text
                        type={isPinned ? 'bodyExtraSmallBold' : 'bodyExtraSmall'}
                        style={styles.pinnedButtonText}
                      >
                        {t('messagesPinned.label')}
                      </Text>
                    </TouchableOpacity>
                  )
                }

                // TypeScript sait maintenant que item est de type MessageItem
                return (
                  <TouchableOpacity
                    style={styles.pinnedContact}
                    onPress={() => handleContactPress(item)}
                  >
                    <Image
                      source={item.avatarUrl ? { uri: item.avatarUrl } : defaultImage}
                      style={styles.pinnedAvatar}
                    />
                    <Text type="bodyExtraSmall" style={styles.pinnedName} numberOfLines={1}>
                      {item.fullName}
                    </Text>
                  </TouchableOpacity>
                )
              }}
              keyExtractor={(item) => item.id}
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.pinnedList}
            />
          )}
        </View>

        {/* Liste des messages */}
        {isLoadingConversations ? (
          <MessageItemSkeletonList count={8} />
        ) : (
          <FlatList
            data={conversations}
            renderItem={renderMessageItem}
            keyExtractor={(item: Conversation) => item.id}
            style={styles.messagesList}
            showsVerticalScrollIndicator={false}
            onEndReached={() => {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage()
              }
            }}
            onEndReachedThreshold={0.5}
          />
        )}
      </View>
      {!isMobile && (
        <View style={commonStyles.flex}>
          {selectedConversation &&
            (selectedConversation.name !== null ||
              selectedConversation.participants.length > 2) && (
              <GroupConversation params={{ conversationId: selectedConversation.id }} />
            )}
          {selectedConversation &&
            selectedConversation.name === null &&
            selectedConversation.participants.length <= 2 && (
              <ConversationScreen
                params={{
                  conversationId: selectedConversation.id,
                }}
              />
            )}
        </View>
      )}
    </SafeAreaView>
  )
}
